import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import { useBulkUpdateAdPropertiesV2 } from "../../../modules/advertising/api/useBulkUpdateAdPropertiesV2";
import { ALGORITHM_OPTIONS } from "../../core/blocks/AdPlan/constants";
import { AdToolPanel } from "../AdToolPanel";
import { Button } from "../Chat";

const FIELD_KEYS = {
  tags: "Tags",
  daily_budget: "Daily Budget",
  target_acos: "Target ACOS",
  max_acos: "Max ACOS",
  target_roas: "Target ROAS",
  min_roas: "Min ROAS",
  bidding_rules: "Bidding Rules",
  budget_rules: "Budget Rules",
  bid_algo: "Bid Algorithm",
  category: "Category",
  bid_adjustments_by_placement: "Placement Bid Adjustments",
  shopper_cohort_bidding: "Audience Bid Multipliers",
  bid_adjustments_by_shopper_segment: "Shopper Segment Bid Adjustments",
  cancel_scheduled_actions: "Scheduled Budget Updates",
  week_parting_method: "Week Parting Method",
  week_parting_multipliers: "Week Parting Multipliers",
  sales_volume_control_slider: "Sales Volume Control",
  non_trellis_enrolled_ad: "Non Trellis Enrollment",
  non_trellis_enrolled_ad_accept_bids: "Non Trellis Accept Bids",
};

const ACTION_KEYS = {
  set: "Set",
  add: "Add",
  remove: "Remove",
  percent: "Adjust",
  fixed: "Adjust",
};

const WEEKDAY_KEYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const PLACEMENT_KEYS = {
  PLACEMENT_TOP: "Top of Search",
  PLACEMENT_PRODUCT_PAGE: "Product Pages",
  PLACEMENT_REST_OF_SEARCH: "Rest of Search",
  HOME: "Home",
  DETAIL_PAGE: "Detail Page",
  OTHER: "Other",
};

const WEEK_PARTING_METHOD_KEYS = {
  0: "default",
  1: "auto",
  2: "manual",
};

const parseValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed.length) return "";

  try {
    return JSON.parse(trimmed);
  } catch {
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) return numeric;
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
  }
  return value;
};

/**
 * @typedef {{action: string, field: string, value: any}} UpdatePayload
 */

const buildDescribeUpdate = (t) => {
  const fieldLabel = (field) => {
    const key = FIELD_KEYS[field];
    return key ? t(($) => $.chat.bulkUpdate.fields[key]) : field;
  };
  const actionLabel = (action) => {
    const key = ACTION_KEYS[action];
    return key ? t(($) => $.chat.bulkUpdate.actions[key]) : action;
  };

  return (update) => {
    const { action, field } = update;
    const value = update.value;
    const label = fieldLabel(field);
    const details = [];

    const asNumber = (input) => (typeof input === "number" ? input : Number(input));
    const ensureArray = (input) => (Array.isArray(input) ? input : []);

    if (field === "week_parting_method") {
      const method = asNumber(value);
      const methodKey = WEEK_PARTING_METHOD_KEYS[method];
      const methodLabel = methodKey ? t(($) => $.chat.bulkUpdate.weekPartingMethods[methodKey]) : (method ?? value);
      return {
        summary: t(($) => $.chat.bulkUpdate["Set Week Parting Method to {method}"], { method: methodLabel }),
        details,
      };
    }

    if (field === "week_parting_multipliers") {
      if (!value) {
        return { summary: t(($) => $.chat.bulkUpdate["Clear Week Parting Multipliers"]), details };
      }

      const entries = ensureArray(value)
        .map((entry) => {
          if (!entry || typeof entry !== "object") return null;
          const weekday = asNumber(entry.weekday);
          let day;
          if (Number.isFinite(weekday) && WEEKDAY_KEYS[weekday]) {
            day = t(($) => $.chat.bulkUpdate.weekdays[WEEKDAY_KEYS[weekday]]);
          } else if (Number.isFinite(weekday)) {
            day = t(($) => $.chat.bulkUpdate["Day {n}"], { n: weekday });
          } else {
            day = t(($) => $.chat.bulkUpdate["Day"]);
          }
          const multiplier = asNumber(entry.multiplier);
          const multiplierText = Number.isFinite(multiplier) ? `${multiplier}x` : `${entry.multiplier ?? ""}`;
          return `${day}: ${multiplierText}`;
        })
        .filter(Boolean);

      return {
        summary: t(($) => $.chat.bulkUpdate["Update Week Parting Multipliers"]),
        details: entries.length ? entries : [t(($) => $.chat.bulkUpdate["Multipliers ready"])],
      };
    }

    if (field === "tags") {
      const tags = ensureArray(value);
      const summary =
        action === "add" ? t(($) => $.chat.bulkUpdate["Add Tags"])
        : action === "remove" ? t(($) => $.chat.bulkUpdate["Remove Tags"])
        : t(($) => $.chat.bulkUpdate["Set Tags"]);
      return {
        summary,
        details: tags.length ? tags.map((tag) => `• ${tag}`) : [t(($) => $.chat.bulkUpdate["No tags provided"])],
      };
    }

    if (["daily_budget", "target_acos", "max_acos", "target_roas", "min_roas"].includes(field)) {
      const numeric = asNumber(value);
      if (action === "percent" && Number.isFinite(numeric)) {
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}%"], { label, value: numeric }),
          details,
        };
      }
      if (action === "fixed" && Number.isFinite(numeric)) {
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}"], { label, value: numeric }),
          details,
        };
      }
      if (Number.isFinite(numeric)) {
        return {
          summary: t(($) => $.chat.bulkUpdate["Set {label} to {value}"], { label, value: numeric }),
          details,
        };
      }
      return {
        summary: t(($) => $.chat.bulkUpdate["{action} {label}"], { action: actionLabel(action), label }),
        details,
      };
    }

    if (["bidding_rules", "budget_rules", "category"].includes(field)) {
      if (value === null || value === "" || value === undefined) {
        return { summary: t(($) => $.chat.bulkUpdate["Clear {label}"], { label }), details };
      }
      return { summary: t(($) => $.chat.bulkUpdate["Set {label} to {value}"], { label, value }), details };
    }

    if (field === "bid_adjustments_by_placement") {
      if (!value) {
        return { summary: t(($) => $.chat.bulkUpdate["Clear Placement Bid Adjustments"]), details };
      }

      if (typeof value === "object") {
        const placements = [];
        Object.values(value).forEach((group) => {
          if (Array.isArray(group)) {
            group.forEach((entry) => {
              if (entry && typeof entry === "object") {
                const placement = entry.placement ?? "";
                const placementKey = PLACEMENT_KEYS[placement];
                const placementName =
                  placementKey ? t(($) => $.chat.bulkUpdate.placements[placementKey])
                  : placement ? placement
                  : t(($) => $.chat.bulkUpdate["Placement"]);
                const percentage = entry.percentage;
                const percentageText = percentage === undefined || percentage === null ? "" : `${percentage}%`;
                placements.push(`${placementName}${percentageText ? `: ${percentageText}` : ""}`);
              }
            });
          }
        });

        return {
          summary: t(($) => $.chat.bulkUpdate["Set Placement Bid Adjustments"]),
          details: placements.length ? placements : [t(($) => $.chat.bulkUpdate["Placement adjustments ready"])],
        };
      }

      return { summary: t(($) => $.chat.bulkUpdate["Set Placement Bid Adjustments"]), details };
    }

    if (field === "bid_adjustments_by_shopper_segment") {
      const count =
        value && typeof value === "object" ?
          Object.values(value).reduce((acc, group) => {
            if (Array.isArray(group)) return acc + group.length;
            return acc + 1;
          }, 0)
        : 0;

      return {
        summary: t(($) => $.chat.bulkUpdate["Set Shopper Segment Bid Adjustments"]),
        details: [
          count ?
            t(($) => $.chat.bulkUpdate["adjustments"], { count })
          : t(($) => $.chat.bulkUpdate["Adjustments ready"]),
        ],
      };
    }

    if (field === "shopper_cohort_bidding") {
      const lines = [];
      if (value && typeof value === "object") {
        Object.values(value).forEach((group) => {
          if (Array.isArray(group)) {
            group.forEach((entry) => {
              if (entry && typeof entry === "object") {
                const name =
                  entry.audienceSegments?.[0]?.name ??
                  entry.shopperCohortType ??
                  t(($) => $.chat.bulkUpdate["Audience"]);
                const percentage = entry.percentage;
                const percentageText = percentage === undefined || percentage === null ? "" : `${percentage}%`;
                lines.push(`${name}${percentageText ? `: ${percentageText}` : ""}`);
              }
            });
          }
        });
      }

      return {
        summary: t(($) => $.chat.bulkUpdate["Set Audience Bid Multipliers"]),
        details: lines.length ? lines : [t(($) => $.chat.bulkUpdate["Audience multipliers ready"])],
      };
    }

    if (field === "bid_algo") {
      const mapAlgorithm = (raw) => {
        if (!raw && raw !== 0) return "";
        const matching = Object.values(ALGORITHM_OPTIONS).find((opt) => opt.value === raw);
        return matching ? matching.label : String(raw);
      };

      let algorithms;
      if (value && typeof value === "object") {
        algorithms = Array.from(
          new Set(
            Object.values(value)
              .map((alg) => mapAlgorithm(alg))
              .filter((alg) => alg && alg.length),
          ),
        );
      } else {
        const mapped = mapAlgorithm(value);
        algorithms = mapped ? [mapped] : [];
      }

      return {
        summary: t(($) => $.chat.bulkUpdate["Set Bid Algorithm"]),
        details:
          algorithms.length ?
            algorithms.map((alg) => t(($) => $.chat.bulkUpdate["Algorithm: {name}"], { name: alg }))
          : [t(($) => $.chat.bulkUpdate["Bid algorithm update ready"])],
      };
    }

    if (field === "sales_volume_control_slider") {
      const values = value && typeof value === "object" ? Object.values(value).map((entry) => String(entry ?? "")) : [];

      return {
        summary: t(($) => $.chat.bulkUpdate["Set Sales Volume Control"]),
        details:
          values.length ?
            values.map((entry) => t(($) => $.chat.bulkUpdate["Value: {value}"], { value: entry }))
          : [t(($) => $.chat.bulkUpdate["Sales volume control update ready"])],
      };
    }

    if (field === "non_trellis_enrolled_ad" || field === "non_trellis_enrolled_ad_accept_bids") {
      const enabled = value === true || value === "true" || value === 1 || value === "1";
      return {
        summary:
          enabled ?
            t(($) => $.chat.bulkUpdate["Enable {label}"], { label })
          : t(($) => $.chat.bulkUpdate["Disable {label}"], { label }),
        details,
      };
    }

    if (field === "cancel_scheduled_actions") {
      return { summary: t(($) => $.chat.bulkUpdate["Cancel Scheduled Budget Updates"]), details };
    }

    if (Array.isArray(value)) {
      const summary =
        action === "add" ? t(($) => $.chat.bulkUpdate["Add {label}"], { label })
        : action === "remove" ? t(($) => $.chat.bulkUpdate["Remove {label}"], { label })
        : t(($) => $.chat.bulkUpdate["{action} {label}"], { action: actionLabel(action), label });
      const lines = value.map((item) => (item === null || item === undefined ? "" : String(item))).filter(Boolean);
      return {
        summary,
        details: lines.length ? lines : [t(($) => $.chat.bulkUpdate["No values supplied"])],
      };
    }

    if (typeof value === "boolean") {
      return {
        summary:
          value ?
            t(($) => $.chat.bulkUpdate["Enable {label}"], { label })
          : t(($) => $.chat.bulkUpdate["Disable {label}"], { label }),
        details,
      };
    }

    if (typeof value === "number") {
      if (action === "percent")
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}%"], { label, value }),
          details,
        };
      if (action === "fixed")
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}"], { label, value }),
          details,
        };
      return {
        summary: t(($) => $.chat.bulkUpdate["Set {label} to {value}"], { label, value }),
        details,
      };
    }

    if (typeof value === "string" && value.length) {
      if (action === "add")
        return {
          summary: t(($) => $.chat.bulkUpdate["Add {label}: {value}"], { label, value }),
          details,
        };
      if (action === "remove")
        return {
          summary: t(($) => $.chat.bulkUpdate["Remove {label}: {value}"], { label, value }),
          details,
        };
      if (action === "percent")
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}%"], { label, value }),
          details,
        };
      if (action === "fixed")
        return {
          summary: t(($) => $.chat.bulkUpdate["Adjust {label} by {value}"], { label, value }),
          details,
        };
      return {
        summary: t(($) => $.chat.bulkUpdate["Set {label} to {value}"], { label, value }),
        details,
      };
    }

    if (value && typeof value === "object") {
      const count = Object.values(value).reduce((acc, entry) => {
        if (Array.isArray(entry)) return acc + entry.length;
        return acc + 1;
      }, 0);
      return {
        summary: t(($) => $.chat.bulkUpdate["{action} {label}"], { action: actionLabel(action), label }),
        details: [
          count ? t(($) => $.chat.bulkUpdate["items"], { count }) : t(($) => $.chat.bulkUpdate["Update ready"]),
        ],
      };
    }

    if ((value === null || value === undefined || value === "") && action === "set") {
      return { summary: t(($) => $.chat.bulkUpdate["Clear {label}"], { label }), details };
    }

    return {
      summary: t(($) => $.chat.bulkUpdate["{action} {label}"], { action: actionLabel(action), label }),
      details,
    };
  };
};

const AdBulkUpdateToolMessage = ({ adIds, updates }) => {
  const { t } = useTranslation(["chat"]);
  const bulkUpdateProperties = useBulkUpdateAdPropertiesV2();

  const normalizedUpdates = useMemo(() => {
    if (!updates) return [];
    const list = Array.isArray(updates) ? updates : [updates];
    return list
      .map((payload) => {
        if (!payload || typeof payload !== "object") return null;
        const { action, field, value } = payload;
        if (!action || !field) return null;
        return {
          action,
          field,
          value: parseValue(value),
        };
      })
      .filter(Boolean);
  }, [updates]);

  const updateDetails = useMemo(() => {
    const describeUpdate = buildDescribeUpdate(t);
    return normalizedUpdates.map((update, index) => {
      const { summary, details } = describeUpdate(update);
      return {
        key: `${update.field}-${update.action}-${index}`,
        summary,
        details,
      };
    });
  }, [normalizedUpdates, t]);

  return (
    <AdToolPanel
      adIds={adIds}
      renderAfterList={() => (
        <div
          className={css({
            width: "full",
            backgroundColor: "#f7f7fa",
            borderRadius: "8px",
            padding: "12px",
            border: "1px solid #e2e2e5",
            fontSize: "12px",
            color: "#333",
            whiteSpace: "normal",
          })}
        >
          <div className={css({ fontSize: "13px", fontWeight: 600, marginBottom: "6px" })}>
            {t(($) => $.chat.bulkUpdate["Bulk update details"])}
          </div>
          {updateDetails.length ?
            <VStack
              gap={3}
              alignItems="flex-start"
              className={css({ maxHeight: "180px", overflowY: "auto", width: "100%" })}
            >
              {updateDetails.map(({ key, summary, details }) => (
                <div key={key} className={css({ fontSize: "12px" })}>
                  <div className={css({ fontWeight: 600 })}>{summary}</div>
                  {details.length ?
                    <VStack gap={1} alignItems="flex-start" className={css({ marginTop: "4px" })}>
                      {details.map((line, detailIdx) => (
                        <div key={`${key}-detail-${detailIdx}`}>{line}</div>
                      ))}
                    </VStack>
                  : null}
                </div>
              ))}
            </VStack>
          : <div>{t(($) => $.chat.bulkUpdate["No bulk update payload provided."])}</div>}
        </div>
      )}
      renderActions={({ ads, isLoading }) => {
        const advertisementIds = (ads ?? []).map((ad) => Number(ad.ad_id)).filter((id) => Number.isFinite(id));
        const handleBulkUpdate = () => {
          if (!advertisementIds.length || !normalizedUpdates.length || bulkUpdateProperties.isPending) return;
          bulkUpdateProperties.mutate({
            advertisements: advertisementIds,
            updates: normalizedUpdates,
          });
        };

        const isButtonDisabled =
          bulkUpdateProperties.isPending || isLoading || !advertisementIds.length || !normalizedUpdates.length;

        const buttonLabel =
          bulkUpdateProperties.isPending ?
            t(($) => $.chat["Updating..."])
          : t(($) => $.chat.bulkUpdate["Apply Bulk Update"]);

        return (
          <Button onClick={handleBulkUpdate} disabled={isButtonDisabled}>
            {buttonLabel}
          </Button>
        );
      }}
    />
  );
};

export { AdBulkUpdateToolMessage };
